import { create } from 'zustand';
import { useUserStore } from './userStore';
import { REPLAY_POINTER_ID, nextChapter, type PageGuide } from '../utils/pageGuide';

/**
 * ⭐ Page guides (owner, 2026-10-10) — the guides of what is on screen, and the one
 * running now.
 *
 * ⭐ PAGES DECLARE THEIR GUIDES (`usePageGuide`) rather than "?" guessing from the
 * view name: Life Map lives inside Reflect → Assessments, so the view alone says
 * "Reflect". Several can be on screen at once — the overview and the open tab's
 * chapter; `registered` is the DEEPEST (highest `level`, latest on ties).
 * ⭐ "?" (Shift+?) offers the same choice as Guide when there is more than one way in
 * (owner, 2026-10-16); with one, it starts `registered`.
 * ⚠️ "Seen" is written only on Done or Skip. Leaving the page mid-guide ends it
 * WITHOUT marking it seen, so it offers itself again next visit.
 * ⭐ After the overview's Done (always), or a chapter's Done / any Skip (until seen
 * once), a closing POINTER dims the app and points at Guide: "replay it any time
 * from here".
 * ⭐ HAND-OFF (owner, 2026-10-10): Done on a guide while the open tab's chapter is
 * still unseen goes STRAIGHT into that chapter — the person never has to discover
 * that tabs have guides. The pointer the overview owed waits until the chapter ends.
 * ⭐ SCENES (2026-10-11): a page may hand `register` an `onScene` — called with a
 * step's `scene` BEFORE that step becomes active, on every move (start, next, back,
 * jump), so the domain/tab it talks about is already open when its card measures.
 * And a `here` — the step for where the person is now, so Guide can offer to start
 * from it.
 */

export interface ActiveGuide {
  guide: PageGuide;
  step: number;
  dir: 1 | -1;
  /** Steps finished this run (moved on from) — ticked in the Steps list. */
  done: number[];
}

/** What a page hands over with its guide. */
export interface GuideHooks {
  /** Set up a step's scene (open a domain, switch a tab) — before the step shows. */
  onScene?: (scene: string) => void;
  /** The step for where the person is now (null = nowhere in particular). */
  here?: () => number | null;
}

interface PageGuideState {
  /** Every guide on screen, in registration order. */
  stack: PageGuide[];
  /** The deepest of them — what "?" starts when it is the only way in (null = none on screen). */
  registered: PageGuide | null;
  active: ActiveGuide | null;
  /** The closing "replay from Guide" pointer is showing. */
  pointer: boolean;
  /** A hand-off deferred the pointer: it shows when the chained chapter ends. */
  pointerOwed: boolean;
  /** Each registered guide's page hooks, by guide id. */
  hooks: Record<string, GuideHooks>;
  /** Returns the unregister function (call it when the page unmounts). */
  register: (guide: PageGuide, hooks?: GuideHooks) => () => void;
  /** Start a guide — the registered one by default — at `step` (default the first). */
  start: (guide?: PageGuide, step?: number) => void;
  /** Go straight to a step of the running guide (the Steps list). */
  jump: (step: number) => void;
  next: () => void;
  back: () => void;
  /** An optional step whose target is missing: move on in the direction of travel. */
  skipMissing: () => void;
  /** Done or Skip — marks this version seen on the account; may show the pointer. */
  finish: (how?: 'done' | 'skip') => void;
  /** End without marking seen (the page went away). */
  stop: () => void;
  dismissPointer: () => void;
}

const deepest = (stack: PageGuide[]): PageGuide | null =>
  stack.reduce<PageGuide | null>((best, g) => (!best || (g.level ?? 0) >= (best.level ?? 0) ? g : best), null);

/** The page sets the step's scene up first — then the step becomes active. */
const setScene = (hooks: Record<string, GuideHooks>, guide: PageGuide, index: number) => {
  const scene = guide.steps[index]?.scene;
  if (scene) hooks[guide.id]?.onScene?.(scene);
};

export const usePageGuideStore = create<PageGuideState>((set, get) => ({
  stack: [],
  registered: null,
  active: null,
  pointer: false,
  pointerOwed: false,
  hooks: {},

  register: (guide, hooks) => {
    const stack = [...get().stack.filter(g => g.id !== guide.id), guide];
    set({ stack, registered: deepest(stack), hooks: { ...get().hooks, [guide.id]: hooks ?? {} } });
    return () => {
      const s = get();
      const rest = s.stack.filter(g => g.id !== guide.id);
      const others = { ...s.hooks };
      delete others[guide.id];
      set({ stack: rest, registered: deepest(rest), hooks: others });
      if (s.active?.guide.id === guide.id) set({ active: null });
    };
  },

  start: (guide, step = 0) => {
    const g = guide ?? get().registered;
    if (!g || !g.steps.length) return;
    const at = Math.max(0, Math.min(step, g.steps.length - 1));
    setScene(get().hooks, g, at);
    set({ active: { guide: g, step: at, dir: 1, done: [] }, pointer: false });
  },

  jump: step => {
    const a = get().active;
    if (!a || step < 0 || step >= a.guide.steps.length || step === a.step) return;
    setScene(get().hooks, a.guide, step);
    set({ active: { ...a, step, dir: step > a.step ? 1 : -1 } });
  },

  next: () => {
    const a = get().active;
    if (!a) return;
    if (a.step >= a.guide.steps.length - 1) { get().finish('done'); return; }
    setScene(get().hooks, a.guide, a.step + 1);
    set({ active: { ...a, step: a.step + 1, dir: 1, done: a.done.includes(a.step) ? a.done : [...a.done, a.step] } });
  },

  back: () => {
    const a = get().active;
    if (!a || a.step === 0) return;
    setScene(get().hooks, a.guide, a.step - 1);
    set({ active: { ...a, step: a.step - 1, dir: -1 } });
  },

  skipMissing: () => {
    const a = get().active;
    if (!a) return;
    const to = a.step + a.dir;
    if (to >= a.guide.steps.length) { get().finish('done'); return; }
    if (to < 0) { setScene(get().hooks, a.guide, 0); set({ active: { ...a, step: 0, dir: 1 } }); return; }
    setScene(get().hooks, a.guide, to);
    set({ active: { ...a, step: to } });
  },

  finish: (how = 'done') => {
    const a = get().active;
    if (!a) return;
    const user = useUserStore.getState();
    user.markPageGuideSeen(a.guide.id, a.guide.version);
    // The overview's Done always ends on the pointer; a chapter's Done or any Skip only
    // until it has been seen — five tabs must not say "replay any time" five times.
    const overview = (a.guide.level ?? 0) === 0;
    const owed = get().pointerOwed || (how === 'done' && overview) || !(user.pageGuidesSeen?.[REPLAY_POINTER_ID] > 0);
    const chained = how === 'done' ? nextChapter(get().stack, a.guide, useUserStore.getState().pageGuidesSeen) : null;
    if (chained) {
      setScene(get().hooks, chained, 0);
      set({ active: { guide: chained, step: 0, dir: 1, done: [] }, pointer: false, pointerOwed: owed });
      return;
    }
    set({ active: null, pointer: owed, pointerOwed: false });
  },

  stop: () => set({ active: null }),

  dismissPointer: () => {
    useUserStore.getState().markPageGuideSeen(REPLAY_POINTER_ID, 1);
    set({ pointer: false });
  },
}));
